import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, filter, map, Observable, pipe, startWith, Subject, switchMap, take, takeUntil, tap, withLatestFrom } from 'rxjs';
import { DevicePollution, DeviceWear, IUpdateServiceRequest, IUpdateServiceStatusRequest, ServiceStatus } from 'src/app/devices/models/service';
import { AppStateInterface } from 'src/app/stores/appState.interface';
import { DeviceServiceStore } from 'src/app/stores/component/device-service-store';
import { DeviceStore } from 'src/app/stores/component/device-store';
import { EmployeeStore } from 'src/app/stores/component/employee-store';
import { currentEmployeeSelector } from 'src/app/stores/global/current-employee/current-employee-selector';
import { IAddDeviceToRevisionRequest, IDeleteDeviceToRevisionRequest, IGetRevisionResponse } from '../../../models/revision';
import * as DeviceCategoryActions from "../../../../stores/global/device-categories/actions";
import { FileRestrictions, UploadEvent } from '@progress/kendo-angular-upload';
import { SelectEvent as LayoutSelectEvent } from '@progress/kendo-angular-layout';
import { AttachmentStore } from 'src/app/stores/component/attachment-store';
import { AttachmentService } from 'src/app/shared/services/attachment.service';
import { IDeviceDetailsResponse, IGetDeviceByIdForRevision } from 'src/app/devices/models/device';
import { RevisionStore } from 'src/app/stores/component/revision-store';
import { downloadBlob } from 'src/app/shared/helpers/fileHelper';
import { ServiceAttachmentService } from 'src/app/devices/services/service-attachment.service';
import { IUpdateServiceAttachment } from 'src/app/devices/models/service-attachment';
import { ActivatedRoute, Router } from '@angular/router';
import { IBaseGetByComanyRequest, IBaseGetByRevisionRequest, IFilter, IGridState, PageSize } from 'src/app/core/models/base';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { DeviceService } from 'src/app/devices/services/device.service';
import { InstallationService } from 'src/app/installations/services/installation.service';
import { deviceCategoriesSelector } from 'src/app/stores/global/device-categories/selectors';
import { HttpResponse } from '@angular/common/http';
import { IUpdateServicePart } from 'src/app/devices/models/service-part';
import { ServicePartService } from 'src/app/devices/services/service-part.service';
import { endOfLocalDay, startOfLocalDay } from 'src/app/shared/helpers/formatDates';


@Component({
    selector: 'app-add-devices',
    templateUrl: './add-devices.component.html',
    styleUrl: './add-devices.component.scss',
    standalone: false
})
export class AddDevicesComponent {

  ////////////////
  // variables //
  //////////////

  @Input() revisionId;
  @Input() revisionStatus;
  @Input() companyId;
  @Input() tenantOnRevision;

  private destroy$ = new Subject<void>();  // Add this


  baseUrl: string;
  attachmentIds: string[];
  thumbnails: Array<{ serviceId: string; attachmentId: string }> = [];
  vm$: Observable<any>;

  public deviceForm: UntypedFormGroup;
  public serviceStatus: Array<{ text: string, value: ServiceStatus }>;
  public deviceWear: Array<{ text: string, value: DeviceWear }>;
  public devicePollution: Array<{ text: string, value: DevicePollution }>;
  
  // Create filter form
  filterForm = this.fb.group({
    installationId: new UntypedFormControl(null),
    deviceCategoryId: new UntypedFormControl(null),
    executionDateFrom: new UntypedFormControl(null),
    executionDateTo: new UntypedFormControl(null),
    keyword: new FormControl(null),
  });

  public currentPartFormGroup: FormGroup | null = null;

  // Upload file type restrictions
  serviceRestrictions: FileRestrictions = {
    allowedExtensions: [".jpg", ".jpeg", ".png"],
  };

  constructor(
    private translocoService: TranslocoService,
    private cdr: ChangeDetectorRef,
    private store: Store<AppStateInterface>,
    private revisionStore: RevisionStore,
    private deviceServiceStore: DeviceServiceStore,
    private deviceStore: DeviceStore,
    private deviceService: DeviceService,
    private fb: FormBuilder,
    private attachmentStore: AttachmentStore,
    private router: Router,
    private route: ActivatedRoute,
    private attachmentService: AttachmentService,
    private serviceAttachmentService : ServiceAttachmentService,
    private installationService: InstallationService,
    private servicePartService: ServicePartService
  ) {
    this.vm$ = combineLatest([
      this.deviceStore.availableDevicesForRevision$,
      this.deviceStore.availableDevicesTotal$,
      this.gridState$,
      this.pageChangeSub$.pipe(
        withLatestFrom(this.searchRequest$),
          map(([pageChangeEvent, searchRequest]) => ({
            pageChangeEvent,
            searchRequest,
        })),
        tap(data => {
          const pageNumber = Math.round(
            (data.pageChangeEvent.skip + data.pageChangeEvent.take) /
            data.pageChangeEvent.take
          );
          this.gridState$.next({ skip: data.pageChangeEvent.skip, pageSize: data.pageChangeEvent.take});
          this.searchRequest$.next({
            ...data.searchRequest,
            pageNumber: pageNumber,
            pageSize: data.pageChangeEvent.take,
          });
        }),
        startWith({skip: 0, take: 10})
      ),
      this.searchRequest$.pipe(
          tap(request => this.deviceStore.getAvailableDevicesForRevision({ 
            revisionId: this.revisionId,
            pageNumber: request.pageNumber,
            pageSize: request.pageSize,
            advancedFilter: request.advancedFilter,
            scheduledDateFrom: request.scheduledDateFrom,
            scheduledDateTo: request.scheduledDateTo,
          })),
          startWith(null)
      ),
      this.store.pipe(select(currentEmployeeSelector)),
      this.store.pipe(select(deviceCategoriesSelector)),
      this.searchInstallationRequest$.pipe(
        filter((request) => !!request.companyId),
        switchMap((request) =>
          this.installationService.getInstallationsByCompany(request)
        ),
        map(x => ({
          data: x.data,
          total: x.totalCount
        } as GridDataResult)),
        startWith(null)
      ),
      this.route.queryParams.pipe(
        tap(params => this.patchFilterForm(params)),
        withLatestFrom(this.searchRequest$),
        map(([params, searchRequest]) => ({ params, searchRequest })),
        tap(data => {
          if (JSON.stringify(data.params) !== "{}") {
            this.onSubmit(data.searchRequest);
          }
        }),
        take(1)
      ),
      this.attachmentStore.isLoading$
    ]).pipe(
      map(([
        availableDevicesForRevision,
        total,
        gridState,
        pageChangeSub,
        searchRequest,
        employee,
        deviceCategories,
        installations,
        filterForm,
        isUploading
      ]) => ({
        availableDevicesForRevision,
        total,
        gridState,
        pageChangeSub,
        searchRequest,
        employee,
        deviceCategories,
        installations,
        filterForm,
        isUploading
      }))
    );
  }

  ngOnInit() {
    this.baseUrl = this.router.url.split('/')[0] + '/devices-admin/';
    this.store.dispatch(DeviceCategoryActions.getDeviceCategories());

    this.searchInstallationRequest$.next({
      ...this.searchInstallationRequest$.getValue(),
      companyId: this.companyId
    });

    this.deviceStore.logStateChanges()
  }

  ////////////////////////
  // (Behavior)Subjects //
  ///////////////////////

  searchRequest$ = new BehaviorSubject<IBaseGetByRevisionRequest>({
    pageNumber: 0,
    pageSize: PageSize.Small,
    revisionId: null
  });

  searchInstallationRequest$ = new BehaviorSubject<IBaseGetByComanyRequest>({
    pageNumber: 0,
    pageSize: 999999,
    companyId: null
  });

  gridState$ = new BehaviorSubject<IGridState>({
    pageSize: PageSize.Small,
    skip: 0,
  });

  pageChangeSub$ = new Subject<PageChangeEvent>();
  


  ///////////////
  // Getters  //
  /////////////

  getControl(path: string) {
    return this.filterForm.get(path) as FormControl;
  }

  getServices(deviceIndex: number): FormArray {
    return this.devices.at(deviceIndex).get('services') as FormArray;
  }

  getParts(deviceIndex: number, serviceIndex: number): FormArray {
    return this.getServices(deviceIndex).at(serviceIndex).get('parts') as FormArray;
  }

  get devices(): FormArray {
    return this.deviceForm.get('devices') as FormArray;
  }

  get deviceControls() {
    return (this.deviceForm.get('devices') as FormArray)?.controls || [];
  }

  get pageSizes() {
    return PageSize;
  }


  ////////////////
  // functions //
  //////////////


  //// form

  // patch url params to form
  patchFilterForm(params: any) {
    this.filterForm.patchValue({
      installationId: params['installationId'] || null,
      deviceCategoryId: params['deviceCategoryId'] || null,
      executionDateFrom: params['executionDateFrom'] != null ? new Date(params['executionDateFrom']) : null,
      executionDateTo: params['executionDateTo'] != null ? new Date(params['executionDateTo']) : null,
      keyword: params['keyword'] || null
    });
  }

  // function on form submit
  onSubmit(request: IBaseGetByRevisionRequest){
    var formValues = this.filterForm.value;

    const searchRequest: IBaseGetByRevisionRequest = {
      ...request,
    };

    // Trim the keyword before using
    const keyword = formValues.keyword ? formValues.keyword.trim() : null;

    // Fields to search for the keyword
    const searchFields = ['serialNumber', 'tagNumber', 'equipmentNumber', 'type', 'qrNumber'];

    // Create keyword filters
    const keywordFilters: IFilter[] = keyword 
        ? searchFields.map(field => ({ field, operator: 'contains', value: keyword })) 
        : [];

    // Other filters
    var filterObject : IFilter = {
      logic: "and",
      filters: []
    }

    var otherFilters: IFilter[] =[]

    if(formValues.installationId)
    {
      otherFilters.push({ field: 'installation.id', operator: 'eq', value: formValues.installationId })
    }

    if(formValues.deviceCategoryId)
    {
      otherFilters.push( { field: 'deviceCategory.id', operator: 'eq', value: formValues.deviceCategoryId })
    }

    // Combine keyword filters with 'or' logic if there are any
    const combinedKeywordFilter: IFilter = { logic: 'or', filters: keywordFilters } 

    // Combine keyword filters with other filters using 'and' logic
    var allFilters: IFilter[] = combinedKeywordFilter.filters.length > 0
        ? [combinedKeywordFilter, ...otherFilters]
        : otherFilters;

    if(formValues.executionDateFrom){
      formValues.executionDateFrom = startOfLocalDay(formValues.executionDateFrom);
      searchRequest.scheduledDateFrom = formValues.executionDateFrom;
    }
    if(formValues.executionDateTo){
      formValues.executionDateTo = endOfLocalDay(formValues.executionDateTo);
      searchRequest.scheduledDateTo = formValues.executionDateTo;
    }

    filterObject.filters = allFilters;

    if(filterObject.filters.length > 0){
      searchRequest.advancedFilter = filterObject;
    }

    this.searchRequest$.next(searchRequest);

    this.updateUrlWithQuery(formValues);
  }

  // update url according to form value changes
  updateUrlWithQuery(formValues){
    // Update URL with the filter parameters
    const queryParams = {
      installationId: formValues.installationId || null,
      deviceCategoryId: formValues.deviceCategoryId || null,
      executionDateFrom: formValues.executionDateFrom || null,
      executionDateTo: formValues.executionDateTo || null,
      keyword: formValues.keyword || null,
    };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
  }
  
  // reset filter preset
  resetFilter(): void {
    // reset form
    this.filterForm.reset();
    
    this.searchRequest$.next({pageNumber: 1,pageSize: this.pageSizes.Small, revisionId: this.revisionId})
    this.gridState$.next({ pageSize: PageSize.Small, skip: 0,})

    // reset url params
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      queryParamsHandling: '',
    });
  }
  
  // Helper function to create a FormGroup for each service part
  createServicePartGroup(part: any): UntypedFormGroup {
    return this.fb.group({
      id: [part.id],
      amountUsed: [part.amountUsed],
      isUsed: [{ value: part.isUsed, disabled: this.revisionStatus?.isFinalStatus }],
      productName: [part.wearingPart.product.name],
      productInternalNumber: [part.wearingPart.product.internalNumber],
      productQuantity: [part.wearingPart.quantity]
    });
  }


  //// device management

  // link device with revision

  linkDevice(id) {

    if(this.revisionStatus.isFinalStatus == false) {
      const addRequest: IAddDeviceToRevisionRequest = {
        revisionId: this.revisionId,
        deviceId: id
      };

      const sr = this.searchRequest$.getValue();
            
      this.deviceStore.addDeviceToRevision({
        request: addRequest,
        revisionId: this.revisionId,
        currentPage: sr.pageNumber,
        pageSize: sr.pageSize,
        advancedFilter: sr.advancedFilter,
        scheduledDateFrom: sr.scheduledDateFrom,
        scheduledDateTo: sr.scheduledDateTo,
      });
    }
  }


  // reset function used by parent page
  resetDevices(): void {
    this.devices.clear();
  }

  // set function used by parent page
  setDevices(deviceNames: string[]): void {
    this.resetDevices();
    deviceNames.forEach((name) => {
      this.devices.push(this.fb.control(name));
    });
  }

  // enable function used by parent page
  enableDevices(): void {
    this.devices.enable();
  }

  // disable function used by parent page
  disableDevices(): void {
    this.devices.disable();
  }


  //// service entry
  // complete Service


  //// tab element
  public onTabSelect(e: LayoutSelectEvent): void {
  }
}
